import { gql, useQuery } from '@apollo/client';
import { ToastId, ToastOptions, useToast } from '@apollo/orbit';
import { orderBy } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect } from 'react';

import { GraphRef, useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { newMinorVersionAvailable } from 'src/app/graph/variantSettingsPage/variantSettingsCards/FederationVersionInfo';
import Config from 'src/lib/config';
import { BuildPipelineTrack } from 'src/lib/graphqlTypes/types';

import { GraphQLTypes } from '../lib/graphqlTypes';

import { useCurrentPlan } from './currentPlanV2Migration';
import { useCurrentAccountId } from './useCurrentAccountId';
import { useLocalStorage } from './useLocalStorage';
import {
  UseNewFedNextVersionAvailableFragment,
  useNewFedNextVersionAvailable,
} from './useNewFedNextVersionAvailable/useNewFedNextVersionAvailable';

export const NewFedVersionToastContainer = ({
  toastId,
  render,
}: {
  toastId: string;
  render: NonNullable<ToastOptions['render']>;
}) => {
  const graphRef = useGraphRef();
  const toast = useToast();
  const toastIdRef = React.useRef<ToastId>();
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const newFedNextVersionAvailable = useNewFedNextVersionAvailable();

  const [newFedVersionToastDismissed, setNewFedVersionToastDismissed] =
    useLocalStorage('newFedVersionToastDismissed');

  const { data } = useQuery<
    GraphQLTypes.NewFedVersionToastQuery,
    GraphQLTypes.NewFedVersionToastQueryVariables
  >(
    gql`
      query NewFedVersionToastQuery($graphId: ID!, $graphVariant: String!) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            allowedTracks {
              badge
              compositionVersion
              buildPipelineTrack
              deprecatedAt
              displayName
              minimumGatewayVersion
              minimumRouterVersion
              notSupportedAt
              federationVersion
            }
            federationVersion
            compositionVersion
            ...UseNewFedNextVersionAvailableFragment
          }
        }
      }
      ${UseNewFedNextVersionAvailableFragment}
    `,
    {
      variables: {
        graphId: graphRef?.graphId as string,
        graphVariant: graphRef?.graphVariant ?? Config.service.defaultVariant,
      },
      skip: !graphRef,
      // this query can sometimes returns bad data where `variant` is set to null, which affects everywhere else in the app that reads from `variant`
      // https://apollograph.slack.com/archives/C08D16WM4FK/p1739288491646679?thread_ts=1739230474.791499&cid=C08D16WM4FK
      fetchPolicy: 'no-cache',
    },
  );

  const service: GraphQLTypes.NewFedVersionToastQuery['service'] =
    data?.service ?? null;
  const variant = service?.variant;
  const latestLaunch = variant?.latestLaunch ?? null;
  const isNewFedNextVersionAvailable =
    variant?.compositionVersion === BuildPipelineTrack.FED_NEXT
      ? newFedNextVersionAvailable({
          latestLaunch,
        })
      : false;

  const addToast = useCallback(() => {
    toastIdRef.current = toast({
      status: 'info',
      id: toastId,
      duration: null,
      isClosable: true,
      onCloseComplete: () => {
        setNewFedVersionToastDismissed([
          ...newFedVersionToastDismissed,
          {
            graphRef: graphRef as GraphRef,
            dismissedAt: Date.now(),
          },
        ]);
      },
      render,
    });
  }, [
    graphRef,
    newFedVersionToastDismissed,
    render,
    setNewFedVersionToastDismissed,
    toast,
    toastId,
  ]);

  useEffect(() => {
    const allowedTracksForVariant = variant?.allowedTracks?.filter(
      (t): t is NonNullable<typeof t> => !!t,
    );
    const variantCompositionVersion = variant?.compositionVersion;

    let newVersionAvailable = false;
    let minDaysSinceDismissed = 30;

    if (variantCompositionVersion === BuildPipelineTrack.FED_NEXT) {
      newVersionAvailable = isNewFedNextVersionAvailable;
      minDaysSinceDismissed = 1;
    } else if (variantCompositionVersion && allowedTracksForVariant) {
      newVersionAvailable = newMinorVersionAvailable(
        allowedTracksForVariant,
        variantCompositionVersion,
      );
    }

    // close the toast if the user upgrades to the latest version
    if (toastIdRef.current && !newVersionAvailable) {
      toast.close(toastIdRef.current);
    } else if (newVersionAvailable && graphRef && !toast.isActive(toastId)) {
      const dismissalsMostRecentFirst = orderBy(
        newFedVersionToastDismissed,
        (obj) => obj.dismissedAt,
        ['desc'],
      );
      const dismissedToastForThisVariant = dismissalsMostRecentFirst.find(
        (obj) =>
          obj.graphRef.graphId === graphRef.graphId &&
          obj.graphRef.graphVariant === graphRef.graphVariant,
      );

      const dismissedToastOver30DaysAgo =
        dismissedToastForThisVariant &&
        moment(Date.now()).diff(
          dismissedToastForThisVariant.dismissedAt,
          'days',
        ) >= minDaysSinceDismissed;

      // Show the toast if:
      // 0. Account type is non-cloudy (cr-3312) AND
      // 1. There is a new stable minor version AND
      // 2. The user hasn't seen this toast before OR
      // 3. It's been >= 30 days since the user has dismissed the toast
      if (
        !currentPlan?.isCloudyAccountType &&
        newVersionAvailable &&
        (!dismissedToastForThisVariant || dismissedToastOver30DaysAgo)
      ) {
        addToast();
      }
    }
  }, [
    addToast,
    currentPlan?.isCloudyAccountType,
    graphRef,
    isNewFedNextVersionAvailable,
    newFedVersionToastDismissed,
    toast,
    toastId,
    variant?.allowedTracks,
    variant?.compositionVersion,
  ]);

  return null;
};
